import React from 'react';
import { useT } from '../../i18n';
import { useOs2, useRangeLabel, PageHead, Card, Stat, NotConnected, money, Th, Td, fmtDateTime, SourceNote, LoadingState, EmptyList } from './os2ui';

/* Finance — "Is the business profitable, and where is the money going?" */

export default function FinanceOsPage() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const { data: d, err, reload, fetchedAt, loading } = useOs2('/finance');
  const { label } = useRangeLabel(isEs);
  if (err) return <p className="text-sm text-red-600">{err}</p>;
  if (!d) return <LoadingState />;

  const r = d.revenue;
  const maxMonth = Math.max(1, ...Object.values(r.by_month || {}));

  return (
    <div data-testid="os2-finance">
      <PageHead
        title={isEs ? 'Finanzas' : 'Finance'}
        question={isEs ? '¿Es rentable el negocio, y a dónde va el dinero?' : 'Is the business profitable, and where is the money going?'}
        fresh={{ at: fetchedAt, onReload: reload, loading, isEs }}
        module="Stripe"
        right={<span className={`text-[11.5px] px-2 py-1 rounded-full border font-semibold ${d.stripe.mode === 'live' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>Stripe {d.stripe.mode}</span>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat label={isEs ? 'Ingresos totales' : 'Total revenue'} value={money(r.total)} testid="fin-total" accent help="revenue" isEs={isEs} scope="lifetime" source="Stripe" />
        <Stat label={isEs ? 'Ingresos' : 'Revenue'} value={money(r.period_total)} testid="fin-period" isEs={isEs} scope="period" source="Stripe" />
        <Stat label="MRR" value={money(r.mrr)} sub={`${d.subscriptions.active} ${isEs ? 'suscripciones activas' : 'active subscriptions'}`} testid="fin-mrr" help="mrr" isEs={isEs} scope="current" source="Stripe" />
        <Stat label="ARR" value={money(r.arr)} sub="MRR × 12" testid="fin-arr" help="arr" isEs={isEs} scope="current" source="Stripe" />
        <Stat label={isEs ? 'Comisiones a partners' : 'Partner payouts'} value={money(d.affiliate_payouts.pending + d.affiliate_payouts.approved)} sub={`${money(d.affiliate_payouts.paid)} ${isEs ? 'pagadas' : 'paid'}`} testid="fin-payouts" help="payouts" isEs={isEs} scope="current" source="Kitxens DB" />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-6" data-testid="fin-by-month">
          <h2 className="text-[15px] font-semibold tracking-tight mb-4">{isEs ? 'Ingresos por mes (histórico)' : 'Revenue by month (lifetime)'}</h2>
          <div className="space-y-2">
            {Object.entries(r.by_month || {}).length ? Object.entries(r.by_month).map(([m, v]) => (
              <div key={m} className="flex items-center gap-3">
                <span className="text-[12px] text-gray-400 tabular-nums w-16">{m}</span>
                <div className="flex-1 h-5 bg-gray-50 rounded overflow-hidden">
                  <div className="h-full bg-[#F77F11]/80 rounded" style={{ width: `${Math.max(4, (v / maxMonth) * 100)}%` }} />
                </div>
                <span className="text-[13px] font-semibold tabular-nums w-16 text-right">{money(v)}</span>
              </div>
            )) : <EmptyList isEs={isEs} text={isEs ? 'Sin pagos registrados aún' : 'No payments recorded yet'} />}
          </div>
          <h2 className="text-[15px] font-semibold tracking-tight mt-6 mb-2">{isEs ? 'Por producto (histórico)' : 'By product (lifetime)'}</h2>
          <div className="flex flex-wrap gap-2">
            {Object.entries(r.by_product || {}).map(([p, v]) => (
              <span key={p} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-50 border border-gray-200 text-[12.5px] text-gray-600 capitalize">
                {p} <b className="tabular-nums text-gray-900">{money(v)}</b>
              </span>
            ))}
          </div>
          <SourceNote source="Stripe" isEs={isEs} className="mt-4" />
        </Card>

        <Card className="overflow-hidden" data-testid="fin-transactions">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-[15px] font-semibold tracking-tight">{isEs ? 'Transacciones recientes' : 'Recent transactions'}</h2>
            <SourceNote source="Stripe" isEs={isEs} />
          </div>
          <table className="w-full">
            <thead><tr><Th>{isEs ? 'Cliente' : 'Customer'}</Th><Th>{isEs ? 'Producto' : 'Product'}</Th><Th>{isEs ? 'Evento' : 'Event'}</Th><Th>{isEs ? 'Importe' : 'Amount'}</Th></tr></thead>
            <tbody>
              {d.transactions.map((t, i) => (
                <tr key={i}>
                  <Td className="max-w-[160px]"><p className="truncate font-medium text-gray-900">{t.name || t.email}</p><p className="text-[11px] text-gray-400">{fmtDateTime(t.at)}</p></Td>
                  <Td className="capitalize">{t.product}</Td>
                  <Td className="text-[11.5px] text-gray-400">{(t.event || '').replace('checkout.session.', '').replace('invoice.', 'inv.')}</Td>
                  <Td className="tabular-nums font-semibold">{money(t.amount)}</Td>
                </tr>
              ))}
              {!d.transactions.length && (
                <tr><Td className="text-center text-gray-400 py-6" colSpan={4}>{isEs ? 'Sin transacciones aún' : 'No transactions yet'}</Td></tr>
              )}
            </tbody>
          </table>
        </Card>
      </div>

      <div className="mt-4 grid grid-cols-2 lg:grid-cols-6 gap-3">
        {Object.keys(d.not_connected).map((k) => (
          <NotConnected key={k} label={k.toUpperCase()} isEs={isEs} testid={`fin-nc-${k}`} />
        ))}
      </div>
    </div>
  );
}
